import { format } from 'date-fns';
import { getDateRangeForTimeframe, toETDate, startOfDayET, endOfDayET } from './dateUtils';
import { Timeframe } from '../types';

/**
 * The calendar day a date-picker value stands for. The picker returns local midnight; reading
 * that instant in Eastern time gave the day before (east of New York) or let the range run into
 * the next day (west of it), so a picked day showed two days of rows.
 */
const pickedDay = (date: Date) => format(date, 'yyyy-MM-dd');

interface FilterOptions {
  selectedDate: Date | null;
  fromDate: Date | null;
  toDate: Date | null;
  selectedTimeframe: Timeframe;
  selectedThirdSectionFilters: Set<string>;
  selectedFourthSectionFilters: Set<string>;
  isWatchListOnly: boolean;
  bookmarkedItems: Set<string>;
}

export const applyDateFilter = (
  items: any[],
  options: Pick<FilterOptions, 'selectedDate' | 'fromDate' | 'toDate' | 'selectedTimeframe'>,
) => {
  let dateRange: { from: Date; to: Date };

  if (options.fromDate && options.toDate) {
    dateRange = {
      from: startOfDayET(pickedDay(options.fromDate)),
      to: endOfDayET(pickedDay(options.toDate)),
    };
  } else if (options.selectedDate) {
    dateRange = {
      from: startOfDayET(pickedDay(options.selectedDate)),
      to: endOfDayET(pickedDay(options.selectedDate)),
    };
  } else {
    dateRange = getDateRangeForTimeframe(options.selectedTimeframe);
  }

  const filtered = items.filter((item, index) => {
    if (!item.date) {
      console.warn(`Item at index ${index} has no date property:`, item);
      return false;
    }

    const itemDateET = toETDate(item.date);
    const inRange = itemDateET >= dateRange.from && itemDateET <= dateRange.to;

    return inRange;
  });

  return filtered;
};

export const applyWatchlistFilter = (
  items: any[],
  isWatchListOnly: boolean,
  bookmarkedItems: Set<string>,
) => {
  if (!isWatchListOnly) return items;

  return items.filter((item) => {
    if (!item.symbol) {
      console.warn('Item missing symbol property:', item);
      return false;
    }
    return bookmarkedItems.has(item.symbol);
  });
};

export const applyGenericFilter = (
  items: any[],
  selectedFilters: Set<string>,
  filterField: string,
  customMatcher?: (item: any, filter: string) => boolean,
) => {
  if (selectedFilters.size === 0 || selectedFilters.has('all')) {
    return items;
  }

  return items.filter((item) => {
    if (!customMatcher && !(filterField in item)) {
      console.warn(`Item missing ${filterField} property:`, item);
      return false;
    }

    return Array.from(selectedFilters).some((filter) => {
      if (customMatcher) {
        return customMatcher(item, filter);
      }
      return item[filterField] === filter;
    });
  });
};

export const applyAllFilters = (
  items: any[],
  options: FilterOptions,
  thirdSectionFilterField: string,
  fourthSectionFilterField: string,
  thirdSectionCustomMatcher?: (item: any, filter: string) => boolean,
  fourthSectionCustomMatcher?: (item: any, filter: string) => boolean,
) => {
  let filteredItems = items;

  filteredItems = applyDateFilter(filteredItems, options);

  filteredItems = applyWatchlistFilter(
    filteredItems,
    options.isWatchListOnly,
    options.bookmarkedItems,
  );

  filteredItems = applyGenericFilter(
    filteredItems,
    options.selectedThirdSectionFilters,
    thirdSectionFilterField,
    thirdSectionCustomMatcher,
  );

  // Apply fourth section filters
  filteredItems = applyGenericFilter(
    filteredItems,
    options.selectedFourthSectionFilters,
    fourthSectionFilterField,
    fourthSectionCustomMatcher,
  );

  return filteredItems;
};
